import { AxiosError } from "axios";
import { NextRouter } from "next/router";
import { Dispatch, SetStateAction } from "react";
import { ErrorReporter } from "../ErrorReporter";
import { ory } from "./sdk/client";
import { logout } from "./actions";
import { isEmailVerified, isSessionActive } from "../../utils/validator";
import { mechaEmailVerificationId } from "../../config/constants";
import { customToast } from "../../utils/customToast";
// A small function to help us deal with errors coming from fetching a flow.
export function handleGetFlowError<S>(
  router: NextRouter,
  flowType:
    | "auth/signup"
    | "settings/my-account"
    | "auth/recovery"
    | "verification"
    | "auth/signin"
    | "settings/confirm-access",
  resetFlow: Dispatch<SetStateAction<S | undefined>>,
  log?: any,
  flowId?: string,
) {
  return async (err: AxiosError) => {
    log.error(
      { err },
      `Error while fetching data from ory {flowId: ${flowId}}`,
    );
    ErrorReporter.collect(
      err,
      `Error while fetching data from ory {flowId: ${flowId}}`,
    );
    const errorData: any = err.response?.data;
    switch (errorData?.error?.id) {
      case "session_inactive":
        await router.push("/auth/signin?return_to=" + window.location.href);
        return;
      case "session_aal2_required":
        if (errorData.redirect_browser_to) {
          const redirectTo = new URL(errorData.redirect_browser_to);
          if (flowType === "settings/my-account") {
            redirectTo.searchParams.set("return_to", window.location.href);
          }
          // 2FA is enabled and enforced, but user did not perform 2fa yet!
          window.location.href = redirectTo.toString();
          return;
        }
        await router.push(
          "/auth/signin?aal=aal2&return_to=" + window.location.href,
        );
        return;
      case "session_already_available":
        // User is already signed in, let's redirect them home!
        try {
          const data = await ory.toSession();
          if (isSessionActive(data?.data)) {
            if (isEmailVerified(data?.data)) {
              await router.push("/");
              return;
            } else {
              sessionStorage.setItem(
                mechaEmailVerificationId,
                encodeURIComponent(data?.data.identity.traits.email),
              );
              await router.push(`/auth/email-verification`);
              return;
            }
          }
        } catch (err) {
          ErrorReporter.collect(
            err,
            `Error while fetching session from ory {flowId: ${flowId}}`,
          );
        }
        await logout();

        return;
      case "session_refresh_required":
        // We need to re-authenticate to perform this action
        window.location.href = errorData.redirect_browser_to;
        return;
      case "self_service_flow_return_to_forbidden":
        // The flow expired, let's request a new one.
        customToast({
          msg: "Session expired, sign in again",
          toastType: "ERROR",
        });
        // resetFlow(undefined);
        await router.push("/" + flowType);
        return;
      case "self_service_flow_expired":
        // The flow expired, let's request a new one.
        customToast({
          msg: "Your interaction expired, please fill out the form again",
          toastType: "ERROR",
        });
        // resetFlow(undefined);
        await router.push("/" + flowType);
        return;
      case "security_csrf_violation":
        // A CSRF violation occurred. Best to just refresh the flow!
        customToast({
          msg: "Your interaction expired, please fill out the form again",
          toastType: "ERROR",
        });
        // resetFlow(undefined);
        await router.push("/" + flowType);
        return;
      case "security_identity_mismatch":
        // The requested item was intended for someone else. Let's request a new flow...
        resetFlow(undefined);
        await router.push("/" + flowType);
        return;
      case "browser_location_change_required":
        // Ory Kratos asked us to point the user to this URL.
        if (flowType === "auth/recovery") {
          if (errorData.redirect_browser_to.includes("/settings/my-account")) {
            const urlSplit = errorData.redirect_browser_to.split("flow=");
            window.location.href = "/auth/reset-password?flow=" + urlSplit[1];
            return;
          }
        }
        window.location.href = errorData.redirect_browser_to;
        return;
      case "session_aal1_required":
        // The flow expired, let's request a new one.
        customToast({
          msg: "Your interaction expired, please fill out the form again",
          toastType: "ERROR",
        });
        resetFlow(undefined);
        await router.push("/" + flowType);
        return;
    }

    switch (err.response?.status) {
      case 410:
        // The flow expired, let's request a new one.
        // resetFlow(undefined);
        await router.push("/" + flowType);
        return;
    }

    // We are not able to handle the error? Return it.
    return Promise.reject(err);
  };
}

// A small function to help us deal with errors coming from initializing a flow.
export const handleFlowError = handleGetFlowError;
